import React, { useState, useRef, useEffect } from 'react';
import { formatTextContent } from '../utils/textformatting';
import '../utils/textformatting.css';
import { API_BASE, authFetch } from '../services/api.js';

const LearningPageChat = ({ 
  currentMicroChunk, 
  currentLanguage,
  passedSubject,
  passedChapter,
  passedTopic,
  passedSubtopic,  // ✅ ADD THIS
  currentChunk     // ✅ ADD THIS
}) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);  // ADD THIS
  const [limitReached, setLimitReached] = useState(false);  // ADD THIS
  const messagesEndRef = useRef(null);

  // Auto scroll to bottom when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading || limitReached) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // GET STUDENT ID HERE - OUTSIDE THE API CALL
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id || 1;
      // ✅ BUILD CONVERSATION HISTORY FROM MESSAGES STATE
      const conversationHistory = messages.map(msg => ({
        sender: msg.sender === 'user' ? 'student' : 'ai',
        text: msg.text
      }));

      // ✅ ADD DEBUG TO VERIFY
      console.log('📜 Sending conversation history:', conversationHistory);
      console.log('📜 History length:', conversationHistory.length);
      
      // Call teaching doubt resolution API
      const response = await authFetch(`${API_BASE}/teaching-doubt/ask?student_id=${studentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: inputText,
          session_id: sessionId,
          student_id: studentId,
          subject_id: passedSubject?.subject_id,
          chapter_id: passedChapter?.chapter_id,
          topic_id: passedTopic?.topic_id,
          subtopic_id: passedSubtopic?.subtopic_id || null,
          chunk_id: currentChunk?.chunk_id || null,
          micro_chunk_id: currentMicroChunk?.micro_chunk_id,
          micro_chunk_content: currentMicroChunk?.content,
          conversation_history: conversationHistory,
          language: currentLanguage
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update session ID if it's a new session
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id);
        }

        // Update follow-up tracking
        // Update follow-up tracking
        setFollowUpCount(data.follow_up_count);
        setLimitReached(data.follow_up_limit_reached);
        
        // Add bot response
        const botMessage = {
          id: Date.now() + 1,
          text: data.answer || data.response,
          sender: 'bot',
          timestamp: new Date(),
          source: data.source || 'ai'
        };

        setMessages(prev => [...prev, botMessage]);
        
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error asking doubt:', error);
      
      // Check if it's the 429 (limit reached) error
      if (error.response && error.response.status === 429) {
        setLimitReached(true);
        const errorMessage = {
          id: Date.now() + 1,
          text: currentLanguage === 'hi'
            ? '⚠️ आपने फॉलो-अप सीमा पूरी कर ली है। कृपया अपने lesson के साथ जारी रखें या नया प्रश्न पूछें।'
            : '⚠️ You\'ve reached the follow-up limit. Please continue with your lesson or ask a new question.',
          sender: 'bot',
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      } else {
        // Add error message
        const errorMessage = {
          id: Date.now() + 1,
          text: currentLanguage === 'hi' 
            ? "खुशी है कि आपने सवाल पूछा! कुछ तकनीकी समस्या है, कृपया दोबारा कोशिश करें।"
            : "Sorry, I encountered an error. Please try again.",
          sender: 'bot',
          timestamp: new Date(),
          isError: true
        };

        setMessages(prev => [...prev, errorMessage]);
      }
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setFollowUpCount(0);
    setLimitReached(false);
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      overflow: 'hidden',
      height: '400px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 20px',
        backgroundColor: '#d53f8c',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{fontSize: '16px', fontWeight: '600'}}>
            💬 Ask Doubts
          </span>
          {messages.length > 0 && (
            <span style={{fontSize: '14px', opacity: 0.9}}>
              {messages.filter(m => m.sender === 'user').length} questions
            </span>
          )}
        </div>

        {messages.length > 0 && (
          <button
            onClick={clearChat}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            🗑️ Clear
          </button>
        )}
      </div>

      {/* Messages Container */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#6b7280',
            fontStyle: 'italic',
            marginTop: '50px'
          }}>
            {currentLanguage === 'hi' 
              ? "कोई भी सवाल पूछिए! मैं यहाँ मदद के लिए हूँ। 💬"
              : "Ask any question! I'm here to help. 💬"}
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '8px'
              }}
            >
              <div style={{
                maxWidth: '80%',
                padding: '12px 16px',
                borderRadius: message.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                backgroundColor: message.sender === 'user' 
                  ? '#667eea' 
                  : message.isError 
                    ? '#fed7d7' 
                    : '#ffffff',
                color: message.sender === 'user' 
                  ? 'white' 
                  : message.isError 
                    ? '#c53030' 
                    : '#2d3748',
                border: message.sender === 'bot' && !message.isError ? '1px solid #e2e8f0' : 'none',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  fontSize: '14px',
                  lineHeight: '1.4',
                  marginBottom: '4px'
                }}className="explanation-content">
                  {formatTextContent(message.text)}
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '6px'
                }}>
                  <span style={{
                    fontSize: '11px',
                    opacity: 0.7
                  }}>
                    {formatTime(message.timestamp)}
                  </span>
                  
                  {message.sender === 'bot' && message.source && !message.isError && (
                    <span style={{
                      fontSize: '10px',
                      opacity: 0.6,
                      fontStyle: 'italic'
                    }}>
                      {message.source === 'ai' ? '🤖' : '📚'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '8px'
          }}>
            <div style={{
              padding: '12px 16px',
              borderRadius: '18px 18px 18px 4px',
              backgroundColor: '#f0f0f0',
              color: '#666'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#999',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}></div>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#999',
                  animation: 'pulse 1.5s ease-in-out infinite 0.3s'
                }}></div>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#999',
                  animation: 'pulse 1.5s ease-in-out infinite 0.6s'
                }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        padding: '15px',
        borderTop: '1px solid #e2e8f0',
        backgroundColor: 'white'
      }}>
        {/* Limit notification */}
        {limitReached && (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>⚠️</span>
            <span>
              {currentLanguage === 'hi'
                ? 'फॉलो-अप सीमा पूर्ण। lesson जारी रखें या नया प्रश्न पूछें।'
                : 'Follow-up limit reached. Continue lesson or ask new question.'}
            </span>
          </div>
        )}
        
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-end'
        }}>

          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              limitReached
                ? (currentLanguage === 'hi' ? "सीमा पूर्ण - Clear करें" : "Limit reached - Clear chat")
                : currentLanguage === 'hi' 
                ? "अपना सवाल यहाँ टाइप करें..."
                : "Type your question here..."
            }
            disabled={isLoading || limitReached}
            style={{
              flex: 1,
              border: '1px solid #d1d5db',
              borderRadius: '20px',
              padding: '12px 16px',
              fontSize: '14px',
              resize: 'none',
              minHeight: '20px',
              maxHeight: '80px',
              outline: 'none',
              backgroundColor: (isLoading || limitReached) ? '#f9fafb' : 'white'
            }}
            rows={1}
          />
          
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading || limitReached}
            style={{
              background: (!inputText.trim() || isLoading || limitReached) ? '#e5e7eb' : '#667eea',
              color: (!inputText.trim() || isLoading || limitReached) ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              cursor: (!inputText.trim() || isLoading || limitReached) ? 'not-allowed' : 'pointer',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            ➤
          </button>
        </div>
      </div>

      {/* CSS for loading animation */}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% {
            opacity: 0.4;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default LearningPageChat;